import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CostSheetPreview } from "./demos/CostSheetPreview";
import { InstagramPreview } from "./demos/InstagramPreview";
import { WhatsAppPreview } from "./demos/WhatsAppPreview";
import { ActionPlanPreview } from "./demos/ActionPlanPreview";
type DemoId = "costos" | "instagram" | "whatsapp" | "plan";
const demos: {
  id: DemoId;
  label: string;
  title: string;
  text: string;
}[] = [{
  id: "instagram",
  label: "Instagram y catálogo",
  title: "Un perfil que se ve profesional",
  text: "Plantillas editables para tus publicaciones y un catálogo prolijo para compartir. Solo cambias la foto, el texto y tus precios."
}, {
  id: "costos",
  label: "Planilla de costos",
  title: "Tus números, claros en minutos",
  text: "Cargas el precio de cada ingrediente una vez y la planilla calcula el costo por receta y por porción. Así defines tu precio con información, no con miedo."
}, {
  id: "whatsapp",
  label: "Mensajes de WhatsApp",
  title: "Responde con seguridad, sin improvisar",
  text: "57 mensajes listos para cada momento de la venta: primera consulta, precios, confirmación, recordatorio de pago, entrega y seguimiento."
}, {
  id: "plan",
  label: "Plan de 30 días",
  title: "Una tarea por día, nada más",
  text: "Cada día sabes exactamente qué hacer. Marcas lo que completaste y ves cómo tu emprendimiento va tomando forma."
}];
const previews: Record<DemoId, React.ReactNode> = {
  costos: <CostSheetPreview data-id="element-240" data-mp="2uty6uvpn9x4i" />,
  instagram: <InstagramPreview data-id="element-241" data-mp="2uwu38flugdqu" />,
  whatsapp: <WhatsAppPreview data-id="element-242" data-mp="38usehc4vbu24" />,
  plan: <ActionPlanPreview data-id="element-243" data-mp="38sxsduhv1vwg" />
};
export function ProductDemoSection() {
  const [active, setActive] = useState<DemoId>("instagram");
  const current = demos.find(d => d.id === active) ?? demos[0];
  return <section className="bg-cream-100 py-14 sm:py-28" data-id="element-244" data-mp="1egrhkwsvph4o">
      <div className="mx-auto max-w-6xl px-5" data-id="element-245" data-mp="19hodbekq3exr">
        <div className="max-w-3xl" data-id="element-246" data-mp="19izhvglzsqpx">
          <h2 className="font-display text-4xl font-semibold leading-tight text-cocoa-900 sm:text-5xl" data-id="element-247" data-mp="39jnwxdipb8ah">
            Mira cómo se ve <em className="font-medium text-caramel-600" data-id="element-248" data-mp="3ehpfzyg4n7ly">por dentro</em>
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-cocoa-700" data-id="element-249" data-mp="3ikcegi7azqs4">
            Herramientas pensadas para usarse, no para quedar guardadas. Estos son ejemplos de lo que vas a encontrar.
          </p>
        </div>

        <div role="tablist" aria-label="Demostraciones del producto" className="-mx-5 mt-10 flex gap-2 overflow-x-auto px-5 pb-2 sm:mx-0 sm:flex-wrap sm:px-0" data-id="element-250" data-mp="3sj1ue5fn315q">
          {demos.map(d => {
          const selected = d.id === active;
          return <button key={d.id} role="tab" id={`tab-${d.id}`} aria-selected={selected} aria-controls={`panel-${d.id}`} onClick={() => setActive(d.id)} className={`shrink-0 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-caramel-500 ${selected ? "bg-cocoa-900 text-cream-50" : "bg-cream-50 text-cocoa-800 ring-1 ring-cocoa-900/10 hover:bg-cream-200"}`} data-id="element-251" data-mp="a49fr1y65cfx">
                {d.label}
              </button>;
        })}
        </div>

        <div role="tabpanel" id={`panel-${current.id}`} aria-labelledby={`tab-${current.id}`} className="mt-8 grid gap-10 rounded-3xl bg-cocoa-900 p-6 sm:p-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-14" data-id="element-252" data-mp="1cxeml1izpihr">
          <AnimatePresence mode="wait" initial={false} data-id="element-253" data-mp="134z2nyo4uju8">
            <motion.div key={`text-${current.id}`} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.18,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-254" data-mp="z4f9f66ok0dd">
              <h3 className="font-display text-3xl font-semibold text-cream-50" data-id="element-255" data-mp="1be3h9adt8slb">{current.title}</h3>
              <p className="mt-4 text-lg leading-relaxed text-cream-200" data-id="element-256" data-mp="1gdzorjlpv25m">{current.text}</p>
            </motion.div>
          </AnimatePresence>
          <AnimatePresence mode="wait" initial={false} data-id="element-257" data-mp="12frksrq9j0ky">
            <motion.div key={`preview-${current.id}`} initial={{
            opacity: 0,
            scale: 0.98
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.98
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} data-id="element-258" data-mp="16es0409j7l37">
              {previews[current.id]}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}